import {
  BellIcon,
  ServerIcon,
  ShieldIcon,
  MessageSquareIcon,
  InfoIcon,
  GitPullRequestIcon,
  NetworkIcon,
  type LucideIcon,
} from 'lucide-react'

import { apiClient } from './api'

// ── Types ──────────────────────────────────────────────────────────────────

export type Notification = {
  id: string
  user_id: string
  organization_id: string
  type: string
  title: string
  message: string
  priority: string // low, normal, high, urgent
  is_read: boolean
  read_at: string | null
  action_url: string | null
  action_label: string | null
  metadata_: Record<string, unknown> | null
  resource_type: string | null
  resource_id: string | null
  expires_at: string | null
  created_at: string
}

export type NotificationListResponse = {
  total: number
  unread_count: number
  notifications: Notification[]
}

export type NotificationUnreadCountResponse = {
  unread_count: number
}

export type NotificationListParams = {
  is_read?: boolean
  type?: string
  portal?: string
  skip?: number
  limit?: number
}

// ── Helpers ────────────────────────────────────────────────────────────────

export function getNotificationIcon(type: string): LucideIcon {
  if (type.startsWith('vm_')) return ServerIcon
  if (type.startsWith('backup_') || type.startsWith('restore_')) return ShieldIcon
  if (type.startsWith('support_')) return MessageSquareIcon
  if (type.startsWith('hub_issue_')) return GitPullRequestIcon
  if (type.startsWith('vpc_')) return NetworkIcon
  if (type === 'system') return InfoIcon
  return BellIcon
}

export function getNotificationTypeColor(type: string): string {
  if (type.startsWith('vm_')) {
    return type.includes('failed') ? 'text-red-500' : 'text-blue-500'
  }
  if (type.startsWith('backup_') || type.startsWith('restore_')) {
    return type.includes('failed') ? 'text-red-500' : 'text-green-500'
  }
  if (type.startsWith('support_')) return 'text-purple-500'
  if (type.startsWith('hub_issue_')) return 'text-teal-500'
  if (type.startsWith('vpc_')) return type.includes('failed') ? 'text-red-500' : 'text-indigo-500'
  if (type === 'system') return 'text-yellow-500'
  return 'text-gray-500'
}

export function getNotificationTypeBgColor(type: string): string {
  if (type.startsWith('vm_')) {
    return type.includes('failed') ? 'bg-red-50' : 'bg-blue-50'
  }
  if (type.startsWith('backup_') || type.startsWith('restore_')) {
    return type.includes('failed') ? 'bg-red-50' : 'bg-green-50'
  }
  if (type.startsWith('support_')) return 'bg-purple-50'
  if (type.startsWith('hub_issue_')) return 'bg-teal-50'
  if (type.startsWith('vpc_')) return type.includes('failed') ? 'bg-red-50' : 'bg-indigo-50'
  if (type === 'system') return 'bg-yellow-50'
  return 'bg-gray-50'
}

export function getPriorityColor(priority: string): string {
  switch (priority) {
    case 'urgent':
      return 'text-red-600'
    case 'high':
      return 'text-orange-500'
    case 'normal':
      return 'text-gray-600'
    case 'low':
      return 'text-gray-400'
    default:
      return 'text-gray-600'
  }
}

function buildQuery(params?: NotificationListParams): string {
  if (!params) return ''
  const qs = new URLSearchParams()
  if (params.is_read !== undefined) qs.set('is_read', String(params.is_read))
  if (params.type) qs.set('type', params.type)
  if (params.portal) qs.set('portal', params.portal)
  if (params.skip !== undefined) qs.set('skip', String(params.skip))
  if (params.limit !== undefined) qs.set('limit', String(params.limit))
  const str = qs.toString()
  return str ? `?${str}` : ''
}

// ── API ────────────────────────────────────────────────────────────────────

export const notificationsApi = {
  list: (params?: NotificationListParams) =>
    apiClient.get<NotificationListResponse>(`/api/v1/notifications${buildQuery(params)}`),

  getUnreadCount: (portal?: string) =>
    apiClient.get<NotificationUnreadCountResponse>(
      `/api/v1/notifications/unread-count${portal ? `?portal=${portal}` : ''}`,
    ),

  markAsRead: (ids?: string[] | null) =>
    apiClient.post('/api/v1/notifications/mark-read', { notification_ids: ids ?? null }),

  markOneAsRead: (id: string) => apiClient.post(`/api/v1/notifications/${id}/read`),

  dismiss: (id: string) => apiClient.delete(`/api/v1/notifications/${id}`),
}

export default notificationsApi
